import { usePOSShopSettings } from './useShopSettings';

/**
 * Hook to get current business type and check feature availability
 */
export function useBusinessType() {
  const { data: settings } = usePOSShopSettings();
  
  const businessType = settings?.shop_type || 'restaurant';
  
  return {
    businessType,
    
    // Feature availability checks
    features: {
      // Kitchen display for food/beverage businesses
      hasKitchenDisplay: ['restaurant', 'cafe', 'bakery'].includes(businessType),
      
      // Table management
      hasTableManagement: businessType === 'restaurant' || 
        (businessType === 'cafe' && settings?.enable_table_service),
      
      // Customer waiting display
      hasCustomerDisplay: ['restaurant', 'cafe'].includes(businessType),
      
      // Barcode scanner for retail/bakery
      hasBarcodeScanner: ['retail', 'bakery'].includes(businessType),
      
      // Batch management for bakery
      hasBatchManagement: businessType === 'bakery',
      
      // Reservations for restaurant
      hasReservations: businessType === 'restaurant',
      
      // Returns for retail
      hasReturns: businessType === 'retail',
      
      // Production planning for bakery
      hasProductionPlanning: businessType === 'bakery',
      
      // Table service workflow
      hasTableService: businessType === 'restaurant' || 
        (businessType === 'cafe' && settings?.enable_table_service),
      
      // Quick counter service (default for most)
      hasCounterService: true, // All business types support counter
    },
    
    // Order type defaults
    defaultOrderType: businessType === 'restaurant' ? 'dine-in' : 'counter',
    
    // Workflow settings
    workflow: {
      // Auto-confirm orders (skip manual confirmation)
      autoConfirm: ['cafe', 'bakery', 'retail'].includes(businessType),
      
      // Require kitchen preparation
      requiresKitchen: ['restaurant', 'cafe', 'bakery'].includes(businessType),
      
      // Support delivery
      supportsDelivery: ['restaurant', 'cafe'].includes(businessType),
      
      // Support takeaway
      supportsTakeaway: ['restaurant', 'cafe', 'bakery'].includes(businessType),
    },
  };
}

/**
 * Get business type label
 */
export function getBusinessTypeLabel(type: string): { en: string; fr: string; ar: string } {
  const labels = {
    restaurant: {
      en: 'Restaurant',
      fr: 'Restaurant',
      ar: 'مطعم',
    },
    cafe: {
      en: 'Cafe',
      fr: 'Café',
      ar: 'مقهى',
    },
    bakery: {
      en: 'Bakery',
      fr: 'Boulangerie',
      ar: 'مخبز',
    },
    retail: {
      en: 'Retail',
      fr: 'Commerce',
      ar: 'تجارة',
    },
  };
  
  return labels[type as keyof typeof labels] || labels.restaurant;
}
